/**
 * Message Bubble Component
 * Displays individual chat messages
 * Matches main app's message bubble design (React Native)
 */

import { Dimensions, Icon, Linking, StyleSheet, Text, useTheme, View } from '@/components/common';
import { useTranslation } from '@/lib/i18n';
import { format } from 'date-fns';
import type { MessageBubbleProps } from '../../types/chat';
import { EmbeddedForm } from '../embedded/EmbeddedForm';
import { EmbeddedTable } from '../embedded/EmbeddedTable';
import { EmbeddedList } from '../embedded/EmbeddedList';
import EmbeddedChart from '../embedded/EmbeddedChart';
import { useChatStore, selectIsLoading } from '../../hooks/useChatStore';

type RichPart =
  | { type: 'text'; text: string }
  | { type: 'link'; text: string; href: string }
  | { type: 'choice'; text: string; value: string; clickable: boolean; emphasized: boolean };

function normalizeChatTextForMobile(content: string): string {
  if (!content) return '';

  return content
    .replace(/\t/g, '  ')
    .split(/\r?\n/)
    .map((line) => {
      const withoutTrailingGap = line.replace(/[ \t]+$/g, '');
      if (!withoutTrailingGap.trim()) return '';

      // LLM output often includes deeply indented continuation/list lines.
      // On narrow screens those preserved spaces create huge visual gutters,
      // so cap display indentation to one small step.
      return withoutTrailingGap.replace(/^[ ]{2,}/, '  ');
    })
    .join('\n');
}

function splitTextIntoTextAndLinks(text: string): Array<{ kind: 'text' | 'link'; value: string }> {
  if (!text) return [];
  // Match full URLs OR internal routes like /chat/cs/intake or /intake
  const re = /(https?:\/\/\S+|\/chat\/[\w\-\/]+\S*|\/intake\b\S*)/g;
  const chunks: Array<{ kind: 'text' | 'link'; value: string }> = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;

  while ((m = re.exec(text)) !== null) {
    const start = m.index;
    const matched = m[0];

    if (start > lastIndex) {
      chunks.push({ kind: 'text', value: text.slice(lastIndex, start) });
    }
    chunks.push({ kind: 'link', value: matched });
    lastIndex = start + matched.length;
  }

  if (lastIndex < text.length) {
    chunks.push({ kind: 'text', value: text.slice(lastIndex) });
  }

  return chunks;
}

function pushTextWithLinks(parts: RichPart[], text: string) {
  for (const chunk of splitTextIntoTextAndLinks(text)) {
    if (chunk.kind === 'text') {
      if (chunk.value) parts.push({ type: 'text', text: chunk.value });
    } else {
      const href = chunk.value;
      parts.push({ type: 'link', text: href, href });
    }
  }
}

function parseRichParts(content: string): RichPart[] {
  if (!content) return [];

  const parts: RichPart[] = [];
  const lines = content.split(/\r?\n/);

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const isNumberedLine = /^\s*["']?\s*\d+\s*[.)]\s*/.test(line);

    // Handle the common "options array" format where items are quoted strings:
    //   [
    //     "1. Occupational Therapy (OT)",
    //     "2. Speech Pathology (SP)",
    //   ]
    // Make these clickable again.
    const quotedNumberedItem = line.match(/^\s*["']\s*(\d+\s*[.)]\s*[^"']+)\s*["']\s*,?\s*$/);
    if (quotedNumberedItem && quotedNumberedItem[1]) {
      const label = quotedNumberedItem[1].trim();
      parts.push({
        type: 'choice',
        text: label,
        value: label,
        clickable: true,
        emphasized: true,
      });
      if (i < lines.length - 1) parts.push({ type: 'text', text: '\n' });
      continue;
    }

    // First, extract [[...]] tokens.
    const bracketRe = /\[\[([^\]]+)\]\]/g;
    let cursor = 0;
    let match: RegExpExecArray | null;

    while ((match = bracketRe.exec(line)) !== null) {
      const start = match.index;
      const end = start + match[0].length;
      const label = (match[1] || '').trim();

      if (start > cursor) {
        pushTextWithLinks(parts, line.slice(cursor, start));
      }

      if (label) {
        parts.push({
          type: 'choice',
          text: label,
          value: label,
          clickable: true,
          emphasized: true,
        });
      } else {
        pushTextWithLinks(parts, match[0]);
      }

      cursor = end;
    }

    const remainder = line.slice(cursor);
    if (!remainder) {
      if (i < lines.length - 1) parts.push({ type: 'text', text: '\n' });
      continue;
    }

    // Then, within the remainder, extract **...** tokens.
    const boldRe = /\*\*([^*]+)\*\*/g;
    let cursor2 = 0;
    let m2: RegExpExecArray | null;
    while ((m2 = boldRe.exec(remainder)) !== null) {
      const start2 = m2.index;
      const end2 = start2 + m2[0].length;
      const label2 = (m2[1] || '').trim();

      if (start2 > cursor2) {
        pushTextWithLinks(parts, remainder.slice(cursor2, start2));
      }

      if (label2) {
        // Only make **...** clickable when it appears in numbered list lines,
        // which is how the prompt defines quick selections.
        parts.push({
          type: 'choice',
          text: label2,
          value: label2,
          clickable: isNumberedLine,
          emphasized: true,
        });
      } else {
        pushTextWithLinks(parts, m2[0]);
      }

      cursor2 = end2;
    }

    if (cursor2 < remainder.length) {
      pushTextWithLinks(parts, remainder.slice(cursor2));
    }

    if (i < lines.length - 1) parts.push({ type: 'text', text: '\n' });
  }

  return parts;
}

function renderSystemMessage(content: string, textStyle: any, linkColor: string) {
  const lines = (content || '').split(/\r?\n/);
  const urlRe = /(https?:\/\/\S+)/;

  return (
    <Text style={textStyle}>
      {lines.map((line, idx) => {
        const match = line.match(urlRe);
        if (!match) {
          return (
            <Text key={`sys_${idx}`}>
              {line}
              {idx < lines.length - 1 ? '\n' : ''}
            </Text>
          );
        }

        const url = match[1];
        const before = line.slice(0, match.index ?? 0);
        const after = line.slice((match.index ?? 0) + url.length);

        const label = (before || '').trim().replace(/[:\-\s]+$/, '');
        const linkText = label ? label : url;
        const prefix = label ? `${label}: ` : before;

        return (
          <Text key={`sys_${idx}`}>
            {prefix}
            <Text
              style={[styles.systemLink, { color: linkColor }]}
              accessibilityRole="link"
              onPress={() => {
                void Linking.openURL(url);
              }}
              suppressHighlighting={true}
            >
              {linkText}
            </Text>
            {after}
            {idx < lines.length - 1 ? '\n' : ''}
          </Text>
        );
      })}
    </Text>
  );
}

function getViewportWidth(): number {
  let measuredWidth = 390;
  if (typeof window !== 'undefined' && Number.isFinite(window.innerWidth) && window.innerWidth > 0) {
    measuredWidth = window.innerWidth;
  } else {
    const width = Dimensions.get('window')?.width;
    measuredWidth = Number.isFinite(width) && width > 0 ? width : 390;
  }

  // Compact iframe captures can report a larger CSS viewport than the actual
  // visible host panel. Cap compact layout math to the mobile design width.
  return measuredWidth < 560 ? Math.min(measuredWidth, 390) : measuredWidth;
}

function getBubbleMaxWidth(params: {
  viewportWidth: number;
  ratio: number;
  reservedWidth: number;
}): number {
  const hardCap = Math.max(120, params.viewportWidth - params.reservedWidth);
  const ratioCap = Math.max(120, params.viewportWidth * params.ratio);
  return Math.floor(Math.max(120, Math.min(hardCap, ratioCap)));
}

export function MessageBubble({ message, isOwn, showTimestamp = true, showSenderName = false }: MessageBubbleProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isLoading = useChatStore(selectIsLoading);
  const viewportWidth = getViewportWidth();

  const senderName =
    message.metadata?.senderName ||
    (message.role === 'assistant'
      ? t('chat.sender.ai', 'AI')
      : message.role === 'user'
        ? t('chat.sender.you', 'You')
        : '');

  const openHref = (href: string) => {
    const trimmed = href.trim();
    if (!trimmed) return;

    if (/^https?:\/\//i.test(trimmed)) {
      void Linking.openURL(trimmed);
      return;
    }

    if (trimmed.startsWith('/')) {
      // Web: make internal paths absolute so Linking can handle it.
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const url = origin ? `${origin}${trimmed}` : trimmed;
      void Linking.openURL(url);
    }
  };

  const textStyle = isOwn
    ? [styles.messageText, { color: '#fff' }]
    : [styles.messageText, { color: theme.colors.onSurface }];

  const sendMessage = useChatStore(state => state.sendMessage);
  const displayContent =
    message.role === 'assistant' || message.role === 'system'
      ? normalizeChatTextForMobile(message.content)
      : message.content;
  const richParts = !isOwn && message.role === 'assistant' ? parseRichParts(displayContent) : null;
  const showAssistantAvatar = !isOwn && message.role === 'assistant';
  const bubbleMaxWidth = isOwn
    ? getBubbleMaxWidth({ viewportWidth, ratio: 0.84, reservedWidth: 72 })
    : getBubbleMaxWidth({
        viewportWidth,
        ratio: 0.92,
        reservedWidth: showAssistantAvatar ? 64 : 24,
      });
  const shouldUseFixedBubbleWidth =
    displayContent.length > 48 || Boolean(message.embeddedComponent);
  const bubbleSizeStyle = shouldUseFixedBubbleWidth
    ? { maxWidth: bubbleMaxWidth, width: bubbleMaxWidth }
    : { maxWidth: bubbleMaxWidth };

  const bubbleStyle = isOwn
    ? [
        styles.bubble,
        styles.bubbleOwn,
        { backgroundColor: theme.colors.primary },
        { alignSelf: 'flex-end' as const, ...bubbleSizeStyle },
      ]
    : [
        styles.bubble,
        styles.bubbleOther,
        {
          backgroundColor: theme.colors.surface,
          borderColor: theme.colors.outlineVariant,
          borderWidth: 1,
        },
        { alignSelf: 'flex-start' as const, ...bubbleSizeStyle },
      ];

  return (
    <View style={[styles.container, isOwn && styles.containerOwn]}>
      {showAssistantAvatar ? (
        <View
          style={[
            styles.avatar,
            styles.assistantAvatar,
            {
              backgroundColor: theme.colors.primaryContainer,
              borderColor: theme.colors.outlineVariant,
            },
          ]}
          accessible
          accessibilityRole="image"
          accessibilityLabel={t('chat.avatar.assistant', 'XEdu assistant')}
        >
          <Icon source="school-outline" size={18} color={theme.colors.primary} />
        </View>
      ) : null}

      {/* Content */}
      <View style={[styles.content, isOwn && styles.contentOwn]}>
        {showSenderName && message.role !== 'system' ? (
          <Text
            variant="labelSmall"
            style={{
              marginBottom: 4,
              color: theme.colors.onSurfaceVariant,
              opacity: 0.85,
              textAlign: isOwn ? 'right' : 'left',
              fontSize: 12,
              lineHeight: 16,
            }}
          >
            {senderName}
          </Text>
        ) : null}
        {/* Message bubble */}
        <View style={bubbleStyle}>
          {richParts ? (
            <Text style={textStyle}>
              {richParts.map((part, idx) => {
                if (part.type === 'text') {
                  return (
                    <Text key={`t_${idx}`}>
                      {part.text}
                    </Text>
                  );
                }

                if (part.type === 'link') {
                  return (
                    <Text
                      key={`p-${idx}`}
                      onPress={() => openHref(part.href)}
                      style={[styles.systemLink, { color: theme.colors.primary }]}
                    >
                      {part.text}
                    </Text>
                  );
                }

                const clickable = part.clickable && !isLoading;
                return (
                  <Text
                    key={`c_${idx}`}
                    onPress={() => {
                      if (!part.clickable) return;
                      if (isLoading) return;
                      sendMessage(part.value);
                    }}
                    style={[
                      part.emphasized && styles.emphasized,
                      part.clickable && styles.choice,
                      part.clickable && { color: theme.colors.primary },
                      !part.clickable && part.emphasized && styles.emphasized,
                      clickable && styles.choiceClickable,
                    ]}
                    accessibilityRole={part.clickable ? 'link' : undefined}
                    suppressHighlighting={true}
                  >
                    {part.text}
                  </Text>
                );
              })}
            </Text>
          ) : (
            message.role === 'system'
              ? renderSystemMessage(displayContent, textStyle, theme.colors.primary)
              : <Text style={textStyle}>{displayContent}</Text>
          )}
        </View>

        {/* Embedded component */}
        {message.embeddedComponent && (
          <View style={styles.embeddedContainer}>
            {renderEmbeddedComponent(message.embeddedComponent)}
          </View>
        )}

        {/* Timestamp */}
        {showTimestamp && (
          <Text 
            variant="labelSmall" 
            style={[
              styles.timestamp, 
              { color: theme.colors.onSurfaceVariant },
              isOwn && styles.timestampOwn
            ]}
          >
            {format(message.timestamp, 'HH:mm')}
          </Text>
        )}
      </View>

      {/* User avatar */}
      {isOwn && (
        <View
          style={[styles.avatar, { backgroundColor: theme.colors.tertiaryContainer }]}
          accessible
          accessibilityRole="image"
          accessibilityLabel={t('chat.avatar.userLabel', 'You')}
        >
          <Text style={[styles.avatarText, { color: theme.colors.onTertiaryContainer }]}>
            {t('chat.avatar.user', 'Y')}
          </Text>
        </View>
      )}
    </View>
  );
}

function renderEmbeddedComponent(component: any) {
  switch (component.type) {
    case 'form':
      return <EmbeddedForm formData={component.data} />;
    case 'table':
      return <EmbeddedTable tableData={component.data} />;
    case 'list':
      return <EmbeddedList listData={component.data} />;
    case 'chart':
      return <EmbeddedChart data={component.data} />;
    default:
      return null;
  }
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 10,
    paddingHorizontal: 0,
    maxWidth: '100%',
  },
  containerOwn: {
    flexDirection: 'row-reverse',
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  assistantAvatar: {
    borderWidth: 1,
    marginTop: 2,
  },
  avatarText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  content: {
    flex: 1,
    flexBasis: 0,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: '100%',
  },
  contentOwn: {
    alignItems: 'flex-end',
  },
  bubble: {
    borderRadius: 12,
    paddingHorizontal: 13,
    paddingVertical: 10,
    minWidth: 0,
    flexShrink: 1,
  },
  bubbleOwn: {
    borderBottomRightRadius: 6,
  },
  bubbleOther: {
    borderBottomLeftRadius: 6,
  },
  messageText: {
    fontSize: 15,
    lineHeight: 23,
    flexShrink: 1,
    letterSpacing: 0,
    ...({
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
    } as any),
  },
  embeddedContainer: {
    marginTop: 8,
    alignSelf: 'stretch',
    minWidth: 0,
  },
  emphasized: {
    fontWeight: '600',
  },
  systemLink: {
    textDecorationLine: 'underline',
    fontWeight: '600',
  },
  choice: {
    textDecorationLine: 'underline',
  },
  choiceClickable: {
    fontWeight: '700',
  },
  timestamp: {
    marginTop: 4,
    opacity: 0.7,
    fontSize: 12,
    lineHeight: 16,
  },
  timestampOwn: {
    textAlign: 'right',
  },
});
